import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex gap-x4 items-center mt-x4 md:mt-x5" }
const _hoisted_2 = ["disabled"]

import ProductDetailPurchaseOptions from '@/shared/product/purchase/ui/ProductPurchaseOptions.vue';
import AddToBasketButtonContent from '@/shared/form/AddToBasketButtonContent.vue';
import { Form } from 'vee-validate';
import { computed, nextTick, ref, Ref, watch } from 'vue';
import utilities from '@/shared/utilities.service';
import { PurchaseOptionType } from '@/types/productServerApi';
import useBasket from '@/storecontext/basket/useBasket';
import useProductClickAndCollect from '@/shared/product/purchase/ui/useProductClickAndCollect';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import useProductBasketButtonState from '@/shared/product/purchase/ui/useProductBasketButtonState';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import ProductPurchaseClickAndCollectStoreSelector
    from '@/shared/product/purchase/ui/ProductPurchaseClickAndCollectStoreSelector.vue';
import ProductPurchaseClickAndCollectStoreInfo
    from '@/shared/product/purchase/ui/ProductPurchaseClickAndCollectStoreInfo.vue';
import useProductDetailUpsell from '@/shared/product/upsell/useProductDetailUpsell';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { hardRedirect } from '@/core/navigation/redirect.service';
import siteContextStore from '@/shared/content/site-context.store';
import useProductAddOns from '@/shared/product/add-ons/useProductAddOns';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';
import { useAddToBasket } from '@/storecontext/product/purchase/useAddToBasket';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductDetailPurchase',
  setup(__props) {

const { productData, variantId } = useProductDetailData();
const { clickAndCollectStoreId } = useBasket();
const {
    addToBasketOnStoreSelection,
    showStoreInfo,
    showStoreSelector,
    storeId,
    openStoreSelector,
    setAddToBasketOnStoreSelection,
    setStoreId,
    setStoreInventoryData,
} = useProductClickAndCollect();
const {
    addClickAndCollect,
    replaceItem,
    changeClickAndCollectStore,
    hasClickAndCollectDeliverable,
    get,
} = useBasket();
const { basketButtonIsDisabled } = useProductBasketButtonState();
const labels = useBlockLabels();
const { maybeInitUpsellItems } = useProductDetailUpsell();
const { addAddOnsToBasket, hasSelectedAddOns, addedAddOnsLabel } = useProductAddOns();
const { addToBasket } = useAddToBasket();
const { setBasketDisabledState } = useProductBasketButtonState();

const editGuid = useRouteQuery(QueryKeys.Edit);
const configuratorId = useRouteQuery(QueryKeys.PDPConfigurationId);

const pending: Ref<boolean> = ref(false);
const timing: number = 2000;

const noAvailablePurchaseOptions = computed(() => productData.value.purchasePossibilities.length < 1);
const basketButtonDefaultText = computed(() => {
    return basketButtonIsDisabled.value || noAvailablePurchaseOptions.value
        ? labels.notInStock
        : editGuid.value
            ? labels.addToBasketUpdate
            : labels.addToBasket;
});
const basketButtonAddedText = computed(() => {
    if (editGuid.value) return labels.addToBasketUpdated;
    if (addedAddOnsLabel.value) return addedAddOnsLabel.value;
    return labels.addedToBasket;
});

const adoptStoreIdFromBasket = (id) => {
    if (id) {
        nextTick(() => setStoreId(id));
    }
};

watch(clickAndCollectStoreId, adoptStoreIdFromBasket, { immediate: true });

const submit = async(form: { purchaseOptionType: PurchaseOptionType }): Promise<void> => {
    basketTrackingService.product(productData.value);

    if (form.purchaseOptionType === PurchaseOptionType.ClickAndCollect && storeId.value === null) {
        openStoreSelector();
        setAddToBasketOnStoreSelection(true);
        return;
    }

    if (!pending.value) {
        pending.value = true;
        const animationTiming = utilities.delay(timing);
        const hasAddedMultiple = hasSelectedAddOns.value;
        try {
            await Promise.all([addOrEdit(form.purchaseOptionType), addAddOnsToBasket(addToBasket, form.purchaseOptionType)]);
            if (form.purchaseOptionType === PurchaseOptionType.ClickAndCollect) {
                await setStoreInventoryData(variantId.value);
            }
            if (!editGuid.value) {
                await maybeInitUpsellItems(form.purchaseOptionType);
            }
            await animationTiming;
        } finally {
            pending.value = false;
            if (hasAddedMultiple) {
                get();
            }
        }
    }
};

const addOrEdit = async(purchaseType: PurchaseOptionType) => {
    if (editGuid.value && purchaseType !== PurchaseOptionType.ModuleConfiguration) {
        await replaceItem({
            lineItemId: editGuid.value,
            targetSku: variantId.value,
        });
        hardRedirect(siteContextStore.settings.basketPageUrl);
    } else if (editGuid.value && purchaseType === PurchaseOptionType.ModuleConfiguration) {
        await addToBasket(variantId.value, purchaseType);
        hardRedirect(siteContextStore.settings.basketPageUrl);
    } else {
        await addToBasket(variantId.value, purchaseType);
    }
};

const onStoreSelection = async(): Promise<void> => {
    if (!pending.value && addToBasketOnStoreSelection.value) {
        await handleAddToBasketOnStoreSelection();
    } else {
        await changeClickAndCollectStore(storeId.value!);
        setBasketDisabledState(false);
    }
    await setStoreInventoryData(variantId.value);
};

const handleAddToBasketOnStoreSelection = async() => {
    pending.value = true;
    const animationTiming = utilities.delay(timing);
    try {
        await addClickAndCollect({
            sku: variantId.value,
            quantity: 1,
            clickAndCollectStoreId: storeId.value!,
        });
        await animationTiming;
    } finally {
        setAddToBasketOnStoreSelection(false);
        pending.value = false;
    }
};

const showAddToBasketButton = computed(() => {
    const showRequestDelivery = productData.value.purchasePossibilities.length === 1 ? productData.value.purchasePossibilities[0].type === PurchaseOptionType.Special : false;
    const showInStoreOnly = productData.value.purchasePossibilities.length === 1 ? productData.value.purchasePossibilities[0].type === PurchaseOptionType.InStoreOnly : false;
    return !showRequestDelivery && !showInStoreOnly;
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Form), { onSubmit: submit }, {
      default: _withCtx(() => [
        (!_unref(editGuid) || _unref(configuratorId))
          ? (_openBlock(), _createBlock(ProductDetailPurchaseOptions, { key: 0 }))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_1, [
          (_unref(showAddToBasketButton))
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "submit",
                disabled: _unref(basketButtonIsDisabled) || _unref(noAvailablePurchaseOptions),
                class: "c-button c-button--primary min-w-220 transition-colors"
              }, [
                _createVNode(AddToBasketButtonContent, {
                  pending: pending.value,
                  "added-button-text": _unref(basketButtonAddedText),
                  "default-button-text": _unref(basketButtonDefaultText)
                }, null, 8, ["pending", "added-button-text", "default-button-text"])
              ], 8, _hoisted_2))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    (_unref(showStoreSelector))
      ? (_openBlock(), _createBlock(ProductPurchaseClickAndCollectStoreSelector, {
          key: 0,
          "basket-has-click-and-collect": _unref(hasClickAndCollectDeliverable),
          onOnStoreSelection: onStoreSelection
        }, null, 8, ["basket-has-click-and-collect"]))
      : _createCommentVNode("", true),
    (_unref(showStoreInfo))
      ? (_openBlock(), _createBlock(ProductPurchaseClickAndCollectStoreInfo, { key: 1 }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})